import React, { type FC, useEffect, useMemo, useState } from 'react'

import cx from 'classnames'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import UserInfo from 'components/UI/UserInfo/UserInfo'
import { useModalContext } from 'context/modalContext'
import {
  cancelSentFriendRequest,
  removeFriend,
  sendFriendRequest,
} from 'services/endpoints/friendship'

import { FriendRequestSentModal } from './FriendshipRequestSentModal/FriendRequestSentModal'
import './_friendRequestModal.scss'

export interface FriendRequestModalProps {
  id: string
  online: boolean
  avatar: string | null
  display_name: string
  username: string
  follower_count: number
  friend_count: number
  close: any
  additionalFn?: any
  message?: any
  mode?: 'request' | 'remove'
  friends_status?: any // "Not friend" | "Pending" | "Friends"
}

const FriendRequestModal: FC<FriendRequestModalProps> = props => {
  const {
    id,
    avatar,
    additionalFn,
    mode = 'request',
    friends_status,
  } = props

  const [message, setMessage] = useState<string>('')
  const [showInput, setShowInput] = useState<boolean>(false)
  const [reqSent, setReqSent] = useState<boolean>(false)

  const isRemoveMode = mode === 'remove'

  const queryClient = useQueryClient()
  const modalData = useModalContext()
  const { t } = useTranslation()

  // normalize once so case/whitespace can't break logic
  const normalizedStatus = useMemo(() => {
    return String(friends_status ?? '').trim().toLowerCase()
  }, [friends_status])

  const isPending = normalizedStatus === 'pending'
  const isFriends = normalizedStatus === 'friends'

  // ==========================
  // SEND / CANCEL (based on friends_status)
  // ==========================
  const sendReq = useMutation(
    async (targetId: string) => {
      if (isPending) {
        await cancelSentFriendRequest(targetId)
        return
      }

      // otherwise: send request
      if (message) {
        return await sendFriendRequest(targetId, message)
      }
      return await sendFriendRequest(targetId)
    },
    {
      onSuccess: () => {
        setReqSent(true)
        queryClient.invalidateQueries(['profile'])
        if (additionalFn) additionalFn()
      },
      onError: () => {
        addToast('error', 'Something went wrong')
      },
    }
  )

  // ==========================
  // REMOVE FRIEND
  // ==========================
  const removeMut = useMutation(
    async (targetId: string) => {
      await removeFriend(targetId)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['profile'])
        modalData.clearModal()
      },
      onError: () => {
        addToast('error', t('Failed to remove friend'))
      },
    }
  )

  // ==========================
  // SUCCESS MODAL AFTER REQUEST ACTION
  // ==========================
  useEffect(() => {
    if (!reqSent || isRemoveMode) return

    // If we were pending, we cancelled — show cancelled message
    if (isPending) {
      modalData.addModal(
        t('friendRequest'),
        <FriendRequestSentModal avatar={avatar} message={t('friendRequestCancelled')} />
      )
      return
    }

    // Otherwise we sent a request
    modalData.addModal(
      t('friendRequest'),
      <FriendRequestSentModal avatar={avatar} />
    )
  }, [reqSent, isRemoveMode, isPending, modalData, t, avatar])

  // ==========================
  // BUTTON LABEL
  // ==========================
  const buttonLabel = isRemoveMode
    ? t('Remove Friend')
    : isPending
      ? t('cancelRequest')
      : isFriends
        ? t('Remove Friend')
        : t('sendRequest')

  return (
    <>
      <div className="friendReqModal__userInfoWrapper">
        <UserInfo user={props} avatar={avatar} showStats={true} />
      </div>

      {/* Hide message input when removing OR when pending (since we're cancelling, not sending) */}
      {!isRemoveMode && !isPending && (
        <div className="friendReqModal__message">
          <p
            className={cx({ inputShown: showInput })}
            onClick={() => setShowInput(!showInput)}
          >
            {t('addMessage')}
          </p>

          {showInput && (
            <textarea
              value={message}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                setMessage(e.currentTarget.value)
              }}
              maxLength={76}
            />
          )}
        </div>
      )}

      <Button
        text={buttonLabel}
        color={isRemoveMode ? 'black' : 'blue'}
        font="mont-14-normal"
        width="fit"
        padding="3"
        height="3"
        customClass="friendReqModal__button"
        clickFn={() => {
          if (isRemoveMode) {
            removeMut.mutate(id)
          } else {
            sendReq.mutate(id)
          }
        }}
      />
    </>
  )
}

export default FriendRequestModal